import type { UploadTelemetry } from "./ports";
import { isValidEtag } from "./multipartValidation";
import { isFullBodySentFailure } from "./blobPutTransport";

export interface S3ChunkTransport {
	put(
		url: string,
		body: Blob,
		options: {
			signal: AbortSignal;
			onProgress?: (bytesUploaded: number) => void;
			headers?: Readonly<Record<string, string>>;
			logContext?: Readonly<Record<string, unknown>>;
		}
	): Promise<{ status: number; etag: string | null; responseBody?: string; retryAfter?: string | null }>;
}

export type RefreshedPresignedUrl = string | { url: string; headers?: Readonly<Record<string, string>> };

export class ChunkNetworkError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "ChunkNetworkError";
	}
}

/** A PUT-side 404 may be a stale target while the multipart upload remains recoverable. */
export class AmbiguousUploadTargetError extends Error {
	readonly status: number;
	readonly code = "AMBIGUOUS_UPLOAD_TARGET";

	constructor(status: number, responseBody?: string) {
		super(`S3 PUT ${status} returned an ambiguous upload target${responseBody ? ` - ${responseBody}` : ""}`);
		this.name = "AmbiguousUploadTargetError";
		this.status = status;
	}
}

export async function projectS3ChunkResponse(response: Response): ReturnType<S3ChunkTransport["put"]> {
	const responseBody = response.ok ? undefined : await response.text().catch(() => "Unable to read error response");
	return {
		status: response.status,
		etag: response.ok ? response.headers.get("ETag") : null,
		retryAfter: response.headers.get("Retry-After"),
		responseBody: response.ok ? undefined : `${response.statusText}${responseBody ? ` - ${responseBody}` : ""}`
	};
}

export type ChunkSourceFailureKind = "unreadable" | "missing" | "storage-failure";

export class FileUnreadableError extends Error {
	readonly fileName: string;

	constructor(fileName: string) {
		super(`The file "${fileName}" could not be read from this device.`);
		this.name = "FileUnreadableError";
		this.fileName = fileName;
	}
}

export interface ChunkSource {
	read(index: number, signal: AbortSignal): Promise<{ ok: true; blob: Blob } | { ok: false; kind: ChunkSourceFailureKind }>;
}

export class MultipartUploadGoneError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "MultipartUploadGoneError";
	}
}

export class UploadCancelledError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "UploadCancelledError";
	}
}

export class UploadOwnershipLostError extends Error {
	constructor() {
		super("Upload lease ownership changed during an awaited operation");
		this.name = "UploadOwnershipLostError";
	}
}

export class TruncatedPartsError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "TruncatedPartsError";
	}
}

/**
 * S3 answered 2xx for a part PUT but the ETag was absent or unreadable (LSP-18742).
 *
 * The part itself very likely LANDED — this is a response-visibility failure, not a transfer
 * failure. Anything that rewrites the response between the browser and S3 strips the header:
 * TLS-inspecting antivirus, a corporate proxy, a VPN, a privacy extension, or a CORS
 * `ExposeHeaders` regression on the bucket. None of those clear up on a retry of the same chunk,
 * which is exactly why this needs its own class.
 *
 * It is BUDGET-BEARING rather than immediately terminal: the engine spends its normal retry
 * allowance first (a genuine one-off proxy hiccup does deserve those attempts), and only the
 * exhausted-budget throw carries this class outward. That is deliberate parity with mobile's
 * `S3ChunkPutLocalFailedException`, whose comment makes the same argument — stay budget-bearing so
 * it "terminalizes at MAX_RETRIES and becomes heal-eligible with a FAILED-state Retry, rather than
 * looping forever".
 *
 * Critically it must NOT be network-class. `isNetworkError` matches only TypeError/AbortError, so
 * this never becomes a ChunkNetworkError, and callers must not treat it as one: network-class
 * failures retry unbounded, and unbounded retry of a never-succeeds condition IS the bug. Before
 * this class existed the engine threw a bare Error, contact-async's classifyFailure fell through to
 * "other", the session was released back to `pending`, and the background drain re-picked it every
 * ~120s with no attempt cap and no elapsed-time budget — 2h38m and still going when production was
 * queried.
 */
export class MissingEtagError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "MissingEtagError";
	}
}

/**
 * The persisted chunk grid's own byte total contradicts session.fileSize (validatePersistedChunkByteIntegrity).
 *
 * A SUBCLASS of TruncatedPartsError deliberately: both apps' classifyFailure already route
 * `instanceof TruncatedPartsError` to the terminal "terminal-local-gaps" kind (round 51 /
 * LSP-17735) and that stays true. What the subclass adds is the ability to tell this failure apart
 * from its sibling — the post-upload "have N/M part etags" refusal (createTruncatedPartsError),
 * whose verdict legitimately depends on what ListParts says the SERVER holds.
 *
 * This one does not: it is a statement about LOCAL bytes that no longer exist anywhere, and both
 * real terminal policies decide handoff-vs-lost purely from server part CONTIGUITY. Feeding it the
 * listParts-bearing `local-gaps` evidence therefore reads a contiguous listing as "the server
 * already holds the whole object" and hands off (contact-admin additionally DELETES the only local
 * copy) for a file the local grid proves is short — the exact fail-open shape
 * reconciliationStateMachine.ts's round 49/81 comment forbids ("terminal AS LOSS, never as the
 * listParts-bearing evidence").
 */
export class PersistedByteIntegrityError extends TruncatedPartsError {
	constructor(message: string) {
		super(message);
		this.name = "PersistedByteIntegrityError";
	}
}

export interface UploadChunkOptions {
	fileName: string;
	sessionId?: string;
	conversationId?: string;
	chunkIndex: number;
	expectedByteLength?: number;
	presignedUrl: string;
	maxRetries: number;
	chunkSource: ChunkSource;
	transport: S3ChunkTransport;
	telemetry: UploadTelemetry;
	refreshPresignedUrl?: (expiredUrl: string, reason?: "expired" | "network") => Promise<RefreshedPresignedUrl>;
	headers?: Readonly<Record<string, string>>;
	waitForOnline?: (signal: AbortSignal) => Promise<void>;
	isOnline?: () => boolean;
	cancelSignal?: AbortSignal;
	onProgress?: (bytesUploaded: number) => void;
	onAttemptStart?: () => void;
	onTransportPutActivityChange?: (isActive: boolean) => void;
	createChunkSourceError?: (kind: ChunkSourceFailureKind) => Error;
	s3ErrorParserMode?: "strict" | "lenient";
	isPresignedUrlExpired?: (url: string) => boolean;
	retryDelay?: (attempt: number, reason: "retry" | "refresh") => Promise<void>;
	reconnectDelay?: () => Promise<void>;
	ensureOwnership?: () => Promise<void>;
	ensureOwnershipAfterRead?: () => Promise<void>;
	/**
	 * LSP-23421: asks the server whether a part whose PUT failed AFTER the browser reported every byte sent is
	 * already stored, before it is sent again. Resolves the server-verified ETag, or null to re-send as before.
	 * Supplied only for write-once v2 parts, whose server receipt is checksum-verified. It must answer null for
	 * its own failures; a throw ends this call, because the caller meant it to.
	 */
	confirmStoredPart?: (error: Error) => Promise<string | null>;
	/**
	 * LSP-23421: the target is signed `If-None-Match: *`, so a 412 means the key already holds the part and
	 * every further PUT is answered 412 too — after uploading the whole body again. Rethrow it at once so the
	 * caller's receipt recovery runs instead of the rest of the retry budget.
	 */
	writeOnceTarget?: boolean;
}

export class S3HttpError extends Error {
	readonly status: number;
	readonly code?: string;

	constructor(status: number, responseBody?: string, code = parseStrictS3ErrorCode(responseBody ?? "") ?? undefined) {
		super(`S3 PUT ${status}${responseBody ? ` ${responseBody}` : ""}`);
		this.name = "S3HttpError";
		this.status = status;
		this.code = code;
	}
}

/**
 * The object store's 400-bearing error codes that describe a TRANSPORT problem rather than a
 * rejection of the content, so a plain re-PUT of the same part can still succeed.
 *
 * S3 spends 400 on both kinds and `classifyUploadFailure`'s status-400 rule cannot tell them apart:
 * it reports `IncompleteBody` (fewer bytes arrived than Content-Length — a dropped mid-body PUT),
 * `SlowDown` (throttling) and `BadDigest` (a checksum a re-send can satisfy) as `terminal`, exactly
 * like a semantic refusal. `RequestTimeout` already classifies as `timeout` upstream; it is listed
 * so this set reads as the whole transport-shaped 400 vocabulary rather than as whatever the marker
 * rules happen to catch today.
 */
const TRANSPORT_SHAPED_S3_ERROR_CODES: ReadonlySet<string> = new Set([
  "incompletebody",
  "requesttimeout",
  "slowdown",
  "baddigest",
]);

/**
 * Does this object-store failure look like a TRANSPORT problem rather than a verdict about the
 * bytes — i.e. must a caller refuse to treat it as permanent, whatever the status arithmetic says?
 *
 * Lives here because it is knowledge about S3's own vocabulary, beside the `S3HttpError` that
 * carries it, so every lane reads one implementation. Two grounds:
 *
 *  - a transport-shaped code (above);
 *  - a 400 with NO parseable `<Error><Code>`. `parseStrictS3ErrorCode` answers null for a body that
 *    is not the store's error XML at all — a WAF challenge, a proxy error page, a truncated
 *    response — and "we could not read the reason" is not evidence of permanence. This package
 *    already records the cost of the opposite reading: a proxy page containing the words "HTTP 403"
 *    once permanently parked a healthy row (see smallFileLane's classifier notes).
 *
 * 410/413/422 are deliberately NOT covered by the code-less rule: unlike 400 — which intermediaries
 * emit constantly — those statuses are unambiguous on their own, and requiring a code for them would
 * stop parking the very rejection LSP-19620 was filed for. Biased toward "not permanent" throughout, because the costs are asymmetric — a missed
 * terminal spends one more retry cycle, a wrong one strands the file and asks the customer to
 * upload it again.
 */
export function isTransportShapedS3Failure(error: unknown): boolean {
  const objectStoreFailure = readObjectStoreFailure(error);
  if (!objectStoreFailure) return false;
  if (TRANSPORT_SHAPED_S3_ERROR_CODES.has(objectStoreFailure.code)) return true;
  return (
    objectStoreFailure.status === 400 && objectStoreFailure.code.length === 0
  );
}

/**
 * The status + code of a failure we can PROVE came from the object store, or `undefined`.
 *
 * Deliberately not `instanceof S3HttpError` alone (AI review, PR #4964). The chunked lane throws
 * that class, but the single-part/direct lane throws contact-admin's own `S3UploadError` — same
 * `status` and `code` fields, different class, and in an app this package cannot import. An
 * `instanceof` gate therefore silently answered "not transport-shaped" for every direct upload,
 * which is exactly the lane a first attempt goes through.
 *
 * Provenance is proven by the store's own typed identities and by the `S3 PUT <status>` sentence
 * both PUT transports author, never by a bare `status`/`code` pair: an API rejection carrying
 * `{ code: 400 }` must not be claimed here, because a `/complete` 400 is a verdict about a manifest
 * and belongs to the API's own handling.
 */
function readObjectStoreFailure(
  error: unknown,
): { status?: number; code: string } | undefined {
  if (error instanceof S3HttpError)
    return {
      status: error.status,
      code: (error.code ?? "").trim().toLowerCase(),
    };
  if (typeof error !== "object" || error === null) return undefined;
  const candidate = error as {
    name?: unknown;
    code?: unknown;
    status?: unknown;
    message?: unknown;
  };
  const name =
    typeof candidate.name === "string" ? candidate.name.toUpperCase() : "";
  const message =
    typeof candidate.message === "string" ? candidate.message : "";
  const fromObjectStore =
    name === "S3UPLOADERROR" ||
    name === "BLOBPUTHTTPERROR" ||
    message.includes("S3 PUT ");
  if (!fromObjectStore) return undefined;
  return {
    status: typeof candidate.status === "number" ? candidate.status : undefined,
    code:
      typeof candidate.code === "string"
        ? candidate.code.trim().toLowerCase()
        : "",
  };
}

const NEVER_ABORT_SIGNAL = new AbortController().signal;

export function parseStrictS3ErrorCode(responseBody: string): string | null {
	const xmlStart = responseBody.indexOf("<");
	if (xmlStart < 0 || typeof DOMParser === "undefined") return null;
	const xml = responseBody.slice(xmlStart);
	if (!/<Error[\s>]/i.test(xml) || !/<\/Error\s*>/i.test(xml)) return null;
	try {
		const parsed = new DOMParser().parseFromString(xml, "application/xml");
		if (parsed.getElementsByTagName("parsererror").length > 0) return null;
		const errorRoot = parsed.getElementsByTagName("Error").item(0);
		if (!errorRoot) return null;
		for (let node = errorRoot.firstChild; node; node = node.nextSibling) {
			if (node.nodeType === 1 && (node as Element).tagName.toLowerCase() === "code") {
				return (node.textContent ?? "").trim() || null;
			}
		}
	} catch {
		return null;
	}
	return null;
}

function parseS3ErrorCode(responseBody: string, mode: "strict" | "lenient"): string | null {
	if (mode === "strict") return parseStrictS3ErrorCode(responseBody);
	const match = /<Code>([^<]+)<\/Code>/.exec(responseBody);
	return match?.[1].trim() || null;
}

function isNetworkError(error: Error): boolean {
	return error instanceof TypeError;
}

function isNetworkErrorLike(error: unknown): boolean {
	if (!(error instanceof Error)) return false;
	if (isNetworkError(error)) return true;
	const originalError = (error as { originalError?: unknown }).originalError;
	return originalError !== undefined && isNetworkErrorLike(originalError);
}

/**
 * Errors whose condition cannot change by retrying the same chunk, and which must therefore outrank
 * the network wrapper at the final throw.
 *
 * Only MissingEtagError qualifies today: MultipartUploadGoneError and UploadOwnershipLostError are
 * rethrown immediately and never reach the end of the retry loop, and TruncatedPartsError is not
 * raised inside it. Written as a predicate rather than an inline instanceof so a future permanent
 * class gets the priority automatically instead of silently inheriting the old last-attempt-wins bug.
 */
function isPermanentChunkError(error: Error): boolean {
	return error instanceof MissingEtagError;
}

function safeErrorMessage(error: Error): string {
	try {
		return typeof error.message === "string" ? error.message : "Upload attempt failed";
	} catch {
		return "Upload attempt failed";
	}
}

function throwIfCancelled(fileName: string, signal?: AbortSignal): void {
	if (signal?.aborted) {
		throw new UploadCancelledError(`Upload of "${fileName}" was cancelled`);
	}
}

export const MAX_CHUNK_RETRY_DELAY_MS = 8_000;

/** Converts Retry-After to an absolute deadline without letting it exceed the caller's retry tier. */
export function retryAfterDeadline(
	value: string | null | undefined,
	now = Date.now(),
	maxDelayMs = Number.MAX_SAFE_INTEGER
): number {
	const header = value?.trim() ?? "";
	const deadline = /^\d+$/.test(header)
		? now + Number(header) * 1000
		: /^[A-Za-z]/.test(header) ? Date.parse(header) : NaN;
	return Number.isSafeInteger(deadline) && Number.isSafeInteger(maxDelayMs) && maxDelayMs >= 0
		? Math.max(0, Math.min(deadline, now + maxDelayMs))
		: 0;
}

export function getChunkRetryDelayMs(attempt: number): number {
	return Math.min(1000 * Math.pow(2, attempt), MAX_CHUNK_RETRY_DELAY_MS);
}

export async function defaultRetryDelay(attempt: number, _reason: "retry" | "refresh"): Promise<void> {
	const delay = getChunkRetryDelayMs(attempt);
	await new Promise((resolve) => setTimeout(resolve, delay));
}

export async function defaultReconnectDelay(): Promise<void> {
	await new Promise((resolve) => setTimeout(resolve, Math.random() * getChunkRetryDelayMs(0)));
}

export async function uploadChunk(options: UploadChunkOptions): Promise<string> {
	const signal = options.cancelSignal ?? NEVER_ABORT_SIGNAL;
	throwIfCancelled(options.fileName, options.cancelSignal);

	let sourceResult: Awaited<ReturnType<ChunkSource["read"]>>;
	try {
		sourceResult = await options.chunkSource.read(options.chunkIndex, signal);
	} finally {
		// Explicit cancellation wins even when a concurrent storage read rejects.
		throwIfCancelled(options.fileName, options.cancelSignal);
	}
	if (!sourceResult.ok) {
		throw options.createChunkSourceError?.(sourceResult.kind) ?? new Error(`Chunk ${options.chunkIndex} source ${sourceResult.kind}`);
	}
	if (sourceResult.blob.size === 0) {
		throw new Error(`Cannot upload a zero-byte chunk for ${options.fileName}`);
	}
	if (options.expectedByteLength !== undefined && sourceResult.blob.size !== options.expectedByteLength) {
		throw new Error(
			`Chunk ${options.chunkIndex} payload size mismatch for ${options.fileName}: expected ${options.expectedByteLength} bytes, received ${sourceResult.blob.size}`
		);
	}
	await options.ensureOwnershipAfterRead?.();

	let currentPresignedUrl = options.presignedUrl;
	let currentHeaders = options.headers;
	let lastError: Error | null = null;
	let lastErrorWasNetwork = false;
	// LSP-18742 (ls-heroku-ci review, high): the FIRST permanent-class error seen anywhere in this
	// chunk's attempt sequence, kept sticky. lastErrorWasNetwork below describes only the most recent
	// attempt, so a mixed sequence (stripped ETag early, a network blip on the budget-exhausting
	// attempt) used to exit network-class and lose the terminal verdict entirely — and neither driver
	// routes ChunkNetworkError to needs-attention, so it retried unbounded and reproduced the very
	// frozen-wheel symptom this class was added to end. A flaky TLS-inspecting proxy plausibly causes
	// both on the same chunk, so the ordering is reachable, not theoretical.
	let permanentError: Error | undefined;
	let attempt = 0;
	let retryNotBefore = 0;
	const retryDelay = options.retryDelay ?? defaultRetryDelay;
	let isRefreshingPresignedUrl = false;
	// Any successful refresh is the regional recovery lane; do not refresh the same regional target
	// again if its first PUT also encounters a transient network failure.
	let hasRegionalPresignedUrl = false;
	// LSP-23421: on a slow uplink a part can send every byte and then never get S3's reply; most such parts are
	// already stored. Ask before re-sending. Only a valid ETag skips the re-send, and a cancel or lost ownership
	// during the question outranks its answer. Null keeps today's re-send; the port converts its own soft
	// failures to null, so anything it throws is deliberate (e.g. a confirmed migration-integrity error).
	const confirmStoredBeforeResend = async (error: Error): Promise<string | null> => {
		if (!options.confirmStoredPart || !isFullBodySentFailure(error)) return null;
		const etag = await options.confirmStoredPart(error);
		throwIfCancelled(options.fileName, options.cancelSignal);
		await options.ensureOwnership?.();
		throwIfCancelled(options.fileName, options.cancelSignal);
		return isValidEtag(etag) ? etag : null;
	};

	while (attempt < options.maxRetries) {
		throwIfCancelled(options.fileName, options.cancelSignal);
		while (Date.now() < retryNotBefore) {
			await new Promise<void>((resolve) => {
				const onAbort = () => { clearTimeout(timer); resolve(); };
				const timer = setTimeout(() => {
					signal.removeEventListener("abort", onAbort);
					resolve();
				}, Math.min(retryNotBefore - Date.now(), MAX_CHUNK_RETRY_DELAY_MS));
				signal.addEventListener("abort", onAbort, { once: true });
			});
			throwIfCancelled(options.fileName, options.cancelSignal);
			await options.ensureOwnership?.();
			throwIfCancelled(options.fileName, options.cancelSignal);
		}
		options.onAttemptStart?.();
		try {
			const transportOptions: {
				signal: AbortSignal;
				onProgress?: (bytesUploaded: number) => void;
				headers?: Readonly<Record<string, string>>;
				logContext?: Readonly<Record<string, unknown>>;
			} = {
				signal,
				onProgress: options.onProgress,
				logContext: {
					sessionId: options.sessionId,
					conversationId: options.conversationId,
					chunkIndex: options.chunkIndex,
					retryAttempt: attempt
				}
			};
			if (currentHeaders !== undefined) transportOptions.headers = currentHeaders;
			options.onTransportPutActivityChange?.(true);
			let result: Awaited<ReturnType<S3ChunkTransport["put"]>>;
			try {
				result = await options.transport.put(currentPresignedUrl, sourceResult.blob, transportOptions);
			} finally {
				options.onTransportPutActivityChange?.(false);
			}
			retryNotBefore =
				result.status === 429 || result.status === 503
					? retryAfterDeadline(result.retryAfter, Date.now(), MAX_CHUNK_RETRY_DELAY_MS)
					: 0;
			throwIfCancelled(options.fileName, options.cancelSignal);

			if (result.status < 200 || result.status >= 300) {
				const responseBody = result.responseBody ?? "";
				const s3ErrorCode = parseS3ErrorCode(responseBody, options.s3ErrorParserMode ?? "strict");
				if (s3ErrorCode === "NoSuchUpload") {
					throw new MultipartUploadGoneError(`S3 PUT ${result.status} NoSuchUpload — multipart upload no longer exists`);
				}
				if (result.status === 404) {
					const ambiguousError = new AmbiguousUploadTargetError(result.status, responseBody);
					if (options.refreshPresignedUrl && attempt < options.maxRetries - 1) {
						lastError = ambiguousError;
						await retryDelay(attempt, "refresh");
						throwIfCancelled(options.fileName, options.cancelSignal);
						await options.ensureOwnership?.();
						throwIfCancelled(options.fileName, options.cancelSignal);
						isRefreshingPresignedUrl = true;
						const refreshed = await options.refreshPresignedUrl(currentPresignedUrl);
						currentPresignedUrl = typeof refreshed === "string" ? refreshed : refreshed.url;
						if (typeof refreshed !== "string") currentHeaders = refreshed.headers;
						isRefreshingPresignedUrl = false;
						attempt++;
						continue;
					}
					throw ambiguousError;
				}
				// LSP-23421: S3 rejects an expired signature (403) before it evaluates `If-None-Match`, so a write-once
				// 412 proves the URL was still valid; refreshing it would only re-send the whole body into another 412.
				const isWriteOnceRefusal = options.writeOnceTarget === true && result.status === 412;
				const isExpiryLike =
					!isWriteOnceRefusal &&
					(s3ErrorCode === "ExpiredToken" ||
						/Request has expired|ExpiredToken|expired/i.test(responseBody) ||
						options.isPresignedUrlExpired?.(currentPresignedUrl) === true);
				if (isExpiryLike && options.refreshPresignedUrl && attempt < options.maxRetries - 1) {
					lastError = new S3HttpError(result.status, responseBody, s3ErrorCode ?? undefined);
					await retryDelay(attempt, "refresh");
					throwIfCancelled(options.fileName, options.cancelSignal);
					await options.ensureOwnership?.();
					throwIfCancelled(options.fileName, options.cancelSignal);
					isRefreshingPresignedUrl = true;
					const refreshed = await options.refreshPresignedUrl(currentPresignedUrl);
					currentPresignedUrl = typeof refreshed === "string" ? refreshed : refreshed.url;
					if (typeof refreshed !== "string") currentHeaders = refreshed.headers;
					isRefreshingPresignedUrl = false;
					attempt++;
					continue;
				}
				throw new S3HttpError(result.status, responseBody, s3ErrorCode ?? undefined);
			}
			if (!isValidEtag(result.etag)) {
				// Thrown INSIDE the try on purpose: the catch below spends the retry budget on it, and the
				// exhausted-budget throw at the end of the loop carries MissingEtagError out to the caller.
				throw new MissingEtagError("S3 response missing ETag header. Verify CORS ExposeHeaders includes ETag.");
			}
			return result.etag;
		} catch (caughtError) {
			throwIfCancelled(options.fileName, options.cancelSignal);
			if (caughtError instanceof MultipartUploadGoneError || caughtError instanceof UploadOwnershipLostError) {
				throw caughtError;
			}
			if (isRefreshingPresignedUrl) {
				throw caughtError;
			}

			const error = caughtError instanceof Error ? caughtError : new Error(String(caughtError));
			lastError = error;
			lastErrorWasNetwork = isNetworkError(error);
			// First one wins: it is the earliest proof that this chunk can never succeed as-is, and a
			// later transient failure does not make that untrue.
			if (permanentError === undefined && isPermanentChunkError(error)) permanentError = error;

			if (options.isOnline?.() !== false) {
				const storedEtag = await confirmStoredBeforeResend(error);
				if (storedEtag) return storedEtag;
			}

			if (error instanceof TypeError && options.waitForOnline && options.isOnline?.() === false) {
				options.telemetry.logInfo("recording.fileUpload.offlineWait", {
					fileName: options.fileName,
					sessionId: options.sessionId,
					conversationId: options.conversationId,
					chunkIndex: options.chunkIndex
				});
				try {
					await options.waitForOnline(signal);
				} catch (waitError) {
					throwIfCancelled(options.fileName, options.cancelSignal);
					throw waitError;
				}
				throwIfCancelled(options.fileName, options.cancelSignal);
				await options.ensureOwnership?.();
				await (options.reconnectDelay ?? defaultReconnectDelay)();
				throwIfCancelled(options.fileName, options.cancelSignal);
				await options.ensureOwnership?.();
				// LSP-23421: the question could not be asked while offline; ask it now, before the re-send.
				const storedEtag = await confirmStoredBeforeResend(error);
				if (storedEtag) return storedEtag;
				continue;
			}
			let regionalRefreshFailed = false;
			if (lastErrorWasNetwork && options.refreshPresignedUrl && !hasRegionalPresignedUrl && attempt < options.maxRetries - 1) {
				await retryDelay(attempt, "refresh");
				throwIfCancelled(options.fileName, options.cancelSignal);
				await options.ensureOwnership?.();
				throwIfCancelled(options.fileName, options.cancelSignal);
				isRefreshingPresignedUrl = true;
				let refreshed: RefreshedPresignedUrl | undefined;
				try {
					refreshed = await options.refreshPresignedUrl(currentPresignedUrl, "network");
				} catch (refreshError) {
					if (refreshError instanceof UploadOwnershipLostError || refreshError instanceof UploadCancelledError) {
						throw refreshError;
					}
					if (!isNetworkErrorLike(refreshError)) {
						throw refreshError;
					}
					// A failed regional refresh is still a network failure; preserve the normal retry/error
					// classification instead of surfacing a batch-refresh validation error.
					isRefreshingPresignedUrl = false;
					regionalRefreshFailed = true;
					refreshed = undefined;
				}
				if (!refreshed) {
					// Fall through to the normal network retry path below.
				} else {
					currentPresignedUrl = typeof refreshed === "string" ? refreshed : refreshed.url;
					if (typeof refreshed !== "string") currentHeaders = refreshed.headers;
					isRefreshingPresignedUrl = false;
					hasRegionalPresignedUrl = true;
					try {
						options.telemetry.logInfo("recording.fileUpload.presignedUrlRefreshed", {
							fileName: options.fileName,
							chunkIndex: options.chunkIndex,
							reason: "network"
						});
					} catch {
						// Recovery must not depend on observability.
					}
					attempt++;
					continue;
				}
			}

			// LSP-23421: a 412 on a write-once target can never become a 2xx, so it ends this call's re-sends.
			const writeOnceRefused = options.writeOnceTarget === true && error instanceof S3HttpError && error.status === 412;
			const hasRetry = attempt < options.maxRetries - 1 && !writeOnceRefused;
			try {
				options.telemetry.logError(
					"recording.fileUpload.chunkAttemptFailed",
					{
						fileName: options.fileName,
						sessionId: options.sessionId,
						conversationId: options.conversationId,
						chunkIndex: options.chunkIndex,
						attempt: attempt + 1,
						maxRetries: options.maxRetries,
						// LSP-18742 (review, medium): a stripped ETag was reported as a generic "http-failure"
						// with httpStatus undefined, indistinguishable in Datadog from any other HTTP error, so a
						// recurrence could only be found by grepping the message text. Its own kind makes it
						// directly greppable — the same reason this repo requires a log line per fail-condition
						// when fixing a silent failure.
						failureKind: lastErrorWasNetwork ? "network" : error instanceof MissingEtagError ? "missing-etag" : "http-failure",
						httpStatus: error instanceof S3HttpError ? error.status : undefined,
						message: safeErrorMessage(error),
						s3Code: error instanceof S3HttpError ? error.code : undefined,
						retryDelayMs: hasRetry ? Math.max(getChunkRetryDelayMs(attempt), retryNotBefore - Date.now()) : undefined,
						...(writeOnceRefused && { writeOnceRefused: true })
					},
					error
				);
			} catch {
				// Attempt telemetry must never replace the transport failure or alter retry behavior.
			}
			// The caller's receipt recovery (driverChunkLoop) answers a write-once 412 from the server's record.
			if (writeOnceRefused) throw error;
			if (hasRetry && !regionalRefreshFailed) {
				await retryDelay(attempt, "retry");
				throwIfCancelled(options.fileName, options.cancelSignal);
				await options.ensureOwnership?.();
			}
			attempt++;
		}
	}

	const finalError = lastError ?? new Error("Chunk upload failed after all retries");
	// Permanent beats transient. Checked BEFORE the network wrapper: a network classification retries
	// unbounded, and unbounded retry of a condition that can never succeed is the whole defect.
	if (permanentError !== undefined) throw permanentError;
	if (lastErrorWasNetwork) throw new ChunkNetworkError(finalError.message);
	throw finalError;
}
